import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import VerifyEmail from './VerifyEmail';
import EmailService from '../../../../services/EmailService';
import { InvalidEmailCodeError } from '../../../../utils/Errors';
import Validations from '../../../../validations/Validations';
import { EmailStore } from '../../../../state/EmailStore';
import { NavButtons } from '../NavButtons/NavButtons';
import TopicTracker from '../../../../tracking/TopicTracker';
import Navigator from '../../../../utils/Navigator';
import Captcha from '../../Captcha/Captcha';
import Environment from '../../../../utils/Environment';

export const VerifyEmailContainer = ({
  isCancelEnabled,
  onError,
  onNextButtonClick
}) => {
  const [digits, setDigits] = useState({
    primaryEmailValue: '',
    secondaryEmailValue: '',
    primaryEmailValid: true,
    secondaryEmailValid: true
  });

  const [verifyEmailErrorMessage, setVerifyEmailErrorMessage] = useState('');

  const [status, setStatus] = useState(
    {
      isLoading: false,
      isEnabled: false
    }
  );
  const [captcha, setCaptcha] = useState({
    value: '',
    isValid: Environment.isDevEnvironment ? true : undefined,
    restart: false
  });

  const [secondInputIsEnabled, setSecondInputIsEnabled] = useState(false);

  const [hints, setHints] = React.useState({
    emailTicketHint: EmailStore.getEmailTicketHint(),
    primaryEmailTicketHint: EmailStore.getPrimaryEmailTicketHint()
  });

  useEffect(() => {
    // if the EmailStore does not have a mergeable email (mostly because the user did a refresh) this will redirect the user to the Emails page.
    if (!EmailStore.getMergeableEmail()) {
      console.warn('EmailStore does not have a mergeable email. Redirecting to Emails page.');
      window.location.href = Navigator.emailsPage;
    }

    // this is to scroll to the top of the page at the beginning of the verify email flow
    window.scrollTo(0, 0);

    TopicTracker.show('email', 'verify-code');
  }, []);

  const validateInput = (value) => {
    const status = (Validations.email.digit || []).map(v => v.validate(value))
      .filter(s => !s.isValid)
      .pop();
    return status || { isValid: true };
  };

  const handleCodeInputChange = (e) => {
    const { errorMessage, isValid } = validateInput(e.target.value);
    setDigits({...digits, primaryEmailValue: e.target.value, primaryEmailValid: isValid});
    setVerifyEmailErrorMessage(errorMessage);
    setSecondInputIsEnabled(isValid);
  };

  const handleSecondCodeInputChange = (e) => {
    const { errorMessage, isValid } = validateInput(e.target.value);
    setDigits({...digits, secondaryEmailValue: e.target.value, secondaryEmailValid: isValid});
    setVerifyEmailErrorMessage(errorMessage);
    setStatus({...status, isEnabled: isValid && digits.primaryEmailValid && captcha.isValid});
  };

  function handleError(error) {
    setStatus({...status, isLoading: false});
    if (error instanceof InvalidEmailCodeError) {
      const {
        emailTicketHint,
        primaryEmailTicketHint,
        primaryToken,
        token
      } = error;
      setDigits({...digits, primaryEmailValue: '', secondaryEmailValue: ''});
      setHints({ emailTicketHint, primaryEmailTicketHint });
      EmailStore.setTokensAndHints(token, emailTicketHint, primaryEmailTicketHint, primaryToken);
      setStatus({...status, isEnabled: false});
    }
    onError(error.message);
  }

  const handleNextButtonClick = () => {
    if(!captcha.isValid) return;
    setStatus({...status, isLoading: true});

    TopicTracker.click('email', 'verify-code-ok');

    if (EmailStore.isMergeActionType()) {
      EmailService.confirmMerge(
        EmailStore.getMergeableEmail(),
        EmailStore.getMergeToken(),
        EmailStore.getEmailTicketHint() + digits.secondaryEmailValue,
        EmailStore.getPrimaryToken(),
        EmailStore.getPrimaryEmailTicketHint() + digits.primaryEmailValue,
        captcha.value
      )
        .then(({ loyaltyMergeStatus, newUserBalance, oldUserBalance, mergeSummary }) => {
          setStatus({...status, isLoading: false});
          EmailStore.setLoyaltyMergeStatus(loyaltyMergeStatus);
          EmailStore.setLoyaltyOldUserBalance(oldUserBalance);
          EmailStore.setLoyaltyNewUserBalance(newUserBalance);
          EmailStore.setLoyaltyMergeSummary(mergeSummary);
          onError('');

          if (EmailStore.hasReminder()) {
            setStatus({...status, isLoading: false});
          } else { // this is only added for the case where is merge but has no reminder (because the next page is the thanks)
            EmailService.merge(EmailStore.getMergeableEmail(), EmailStore.getLoyaltyMergeStatus())
              .then(() => {
                setStatus({...status, isLoading: false});
              })
              .catch((error) => {
                setStatus({...status, isLoading: false});
                onError(error.message);
              });
          }

          onNextButtonClick();
        })
        .catch((error) => {
          handleError(error);
        });
    } else {
      EmailService.confirmEmail(
        EmailStore.getMergeableEmail(),
        EmailStore.getMergeToken(),
        EmailStore.getEmailTicketHint() + digits.secondaryEmailValue,
        EmailStore.getPrimaryToken(),
        EmailStore.getPrimaryEmailTicketHint() + digits.primaryEmailValue,
        captcha.value
      )
        .then(() => {
          setStatus({...status, isLoading: false});
          onNextButtonClick();
          onError('');
        })
        .catch((error) => {
          handleError(error);
        });
    }
    // Reset captcha after submit
    setTimeout(() => { setCaptcha({...captcha, restart: false}) }, 1000);
  };

  const handleCancelClickButton = () => {
    TopicTracker.click('email', 'verify-code-cancel');
  };

  const handleCaptchaCallback = (response) => {
    console.info('Handling valid captcha callback in VERIFY EMAIL form');
    setCaptcha({...captcha, value: response, isValid: true});
    setStatus({...status, isEnabled: digits.primaryEmailValid && digits.secondaryEmailValid});
  }

  const handleCaptchaExpires = () => {
    console.info('Handling expire captcha callback in VERIFY EMAIL form');
    setCaptcha({...captcha, value: '', isValid: false});
  }

  return (
    <Fragment>
      <VerifyEmail
        digits={digits}
        statusIsLoading={status.isLoading}
        statusSecondInputIsEnabled={secondInputIsEnabled}
        onCodeInputChange={handleCodeInputChange}
        onSecondCodeInputChange={handleSecondCodeInputChange}
        hints={hints}
        errorMessage={verifyEmailErrorMessage}
      />
      { !Environment.isDevEnvironment &&
        <div className="-mt-3 -mb-5">
          <Captcha
            uniqueName="verifyEmail"
            isValid={captcha.isValid}
            onCallback={handleCaptchaCallback}
            onExpires={handleCaptchaExpires}
            restart={captcha.restart}
          />
        </div>
      }
      <NavButtons
        isCancelEnabled={isCancelEnabled}
        isLoading={status.isLoading}
        onClick={handleNextButtonClick}
        onClickCancel={handleCancelClickButton}
        isDisabled={status.isLoading || !status.isEnabled}
      />
    </Fragment>
  );
};

VerifyEmailContainer.propTypes = {
  isCancelEnabled: PropTypes.bool.isRequired,
  onNextButtonClick: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired
};
